import {
  CalendarDate,
  parseDate,
  getLocalTimeZone,
  today
} from '@internationalized/date'

import { DayOfWeek, ShippingBlackoutConfig } from 'src/apollo/onlineOrdering'

/**
 * Calculate the arrival date from a ship date by adding estimatedDays business days.
 * Skips weekends (Saturday and Sunday) as mail doesn't process those days.
 */
export const calculateArrivalDate = (
  shipDate: CalendarDate,
  estimatedDays: number | undefined,
  timezone: string = getLocalTimeZone()
): CalendarDate => {
  if (estimatedDays === undefined || estimatedDays <= 0) {
    return shipDate
  }

  let arrivalDate = shipDate
  let daysAdded = 0

  while (daysAdded < estimatedDays) {
    arrivalDate = arrivalDate.add({ days: 1 })
    const dayOfWeek = arrivalDate.toDate(timezone).getDay()
    // Skip Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++
    }
  }

  return arrivalDate
}

/**
 * Calculate the ship date from an arrival date by subtracting estimatedDays business days.
 * This is the inverse of calculateArrivalDate.
 * Note: Only subtracts transit time, NOT the "+1 ship day" or "+1 past 5PM" adjustments
 * Those adjustments only affect minDate (earliest selectable arrival date)
 */
export const calculateShipDate = (
  arrivalDate: CalendarDate,
  estimatedDays: number | undefined,
  timezone: string = getLocalTimeZone()
): CalendarDate => {
  if (estimatedDays === undefined || estimatedDays <= 0) {
    return arrivalDate
  }

  let shipDate = arrivalDate
  let daysSubtracted = 0

  while (daysSubtracted < estimatedDays) {
    shipDate = shipDate.subtract({ days: 1 })
    const dayOfWeek = shipDate.toDate(timezone).getDay()
    // Skip Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysSubtracted++
    }
  }

  return shipDate
}

/**
 * Calculate the arrival date from a ship date string (ISO format).
 * Returns the arrival date as an ISO string with the original time portion preserved.
 */
export const calculateArrivalDateFromString = (
  shipDateStr: string,
  estimatedDays: number | undefined,
  timezone: string = getLocalTimeZone()
): string => {
  if (estimatedDays === undefined || estimatedDays <= 0) {
    return shipDateStr
  }

  try {
    const datePart = shipDateStr.split('T')[0]
    if (!datePart) return shipDateStr

    const shipDate = parseDate(datePart)
    const arrivalDate = calculateArrivalDate(shipDate, estimatedDays, timezone)

    // Return as ISO string with the original time portion
    const timePart = shipDateStr.split('T')[1] ?? '00:00:00Z'
    return `${arrivalDate.toString()}T${timePart}`
  } catch {
    return shipDateStr
  }
}

/**
 * Calculate the minimum selectable arrival date based on estimated transit days.
 * This accounts for:
 * - Transit time (estimatedDays)
 * - +1 for the shipping day itself (we can't ship and arrive same day)
 * - +1 if current time is past 5PM (shipping cutoff - can't start processing today)
 * - Skips weekends (Saturday and Sunday) as mail doesn't process those days
 */
export const calculateMinArrivalDate = (
  estimatedDays: number | undefined,
  timezone: string = getLocalTimeZone()
): CalendarDate => {
  const todayDate = today(timezone)

  if (estimatedDays === undefined) {
    return todayDate
  }

  // Check if current time is past 5PM (shipping cutoff)
  const now = new Date()
  const isPast5PM = now.getHours() >= 17

  // Total business days to add:
  // - estimatedDays (transit time)
  // - +1 for the shipping day (we don't get today)
  // - +1 if past 5PM (can't start processing today)
  const businessDaysToAdd = estimatedDays + 1 + (isPast5PM ? 1 : 0)

  return calculateArrivalDate(todayDate, businessDaysToAdd, timezone)
}

/**
 * Map JavaScript getDay() result (0-6) to DayOfWeek enum
 */
const dayOfWeekMap: Record<number, DayOfWeek> = {
  0: DayOfWeek.Sunday,
  1: DayOfWeek.Monday,
  2: DayOfWeek.Tuesday,
  3: DayOfWeek.Wednesday,
  4: DayOfWeek.Thursday,
  5: DayOfWeek.Friday,
  6: DayOfWeek.Saturday
}

type DateComponents = { year: number; month: number; day: number }

/**
 * Parse a date string (YYYY-MM-DD or ISO format) and extract year, month, day components.
 * This avoids timezone issues by parsing the string directly.
 */
const parseDateComponents = (dateStr: string): DateComponents => {
  // Handle both 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:mm:ss' formats
  const datePart = dateStr.split('T')[0]
  const parts = datePart?.split('-')
  return {
    year: parseInt(parts?.[0] ?? '0', 10),
    month: parseInt(parts?.[1] ?? '0', 10) - 1, // Convert to 0-indexed month
    day: parseInt(parts?.[2] ?? '0', 10)
  }
}

/**
 * Convert date components to a comparable numeric value (MMDD format for recurring, YYYYMMDD for non-recurring)
 */
const toMonthDayNumber = (month: number, day: number): number =>
  month * 100 + day

const toFullDateNumber = (year: number, month: number, day: number): number =>
  year * 10000 + (month + 1) * 100 + day

/**
 * Check if a month/day falls within a same-year recurring range (e.g., Mar 1 - Mar 15)
 */
const isInSameYearRecurringRange = (
  dateMonthDay: number,
  startMonthDay: number,
  endMonthDay: number
): boolean => dateMonthDay >= startMonthDay && dateMonthDay <= endMonthDay

/**
 * Check if a month/day falls within a year-spanning recurring range (e.g., Dec 20 - Jan 5)
 * This means the date is either after the start (in the first year) or before the end (in the next year)
 */
const isInYearSpanningRecurringRange = (
  dateMonthDay: number,
  startMonthDay: number,
  endMonthDay: number
): boolean => dateMonthDay >= startMonthDay || dateMonthDay <= endMonthDay

/**
 * Check if a date falls within a recurring blackout event's date range.
 * Compares only month and day, ignoring year.
 */
const isInRecurringBlackoutRange = (
  dateMonth: number,
  dateDay: number,
  startDate: DateComponents,
  endDate: DateComponents
): boolean => {
  const dateMonthDay = toMonthDayNumber(dateMonth, dateDay)
  const startMonthDay = toMonthDayNumber(startDate.month, startDate.day)
  const endMonthDay = toMonthDayNumber(endDate.month, endDate.day)

  // Determine if this is a same-year range (e.g., Mar 1 - Mar 15) or year-spanning (e.g., Dec 20 - Jan 5)
  const isSameYearRange = startMonthDay <= endMonthDay

  return isSameYearRange
    ? isInSameYearRecurringRange(dateMonthDay, startMonthDay, endMonthDay)
    : isInYearSpanningRecurringRange(dateMonthDay, startMonthDay, endMonthDay)
}

/**
 * Check if a date falls within a blackout event's date range.
 * Handles both one-time and recurring events.
 */
const isDateInBlackoutEvent = (
  date: CalendarDate,
  startDateStr: string,
  endDateStr: string,
  isRecurring: boolean
): boolean => {
  const startDate = parseDateComponents(startDateStr)
  const endDate = parseDateComponents(endDateStr)
  const dateMonth = date.month - 1 // Convert to 0-indexed for consistency

  if (isRecurring) {
    return isInRecurringBlackoutRange(dateMonth, date.day, startDate, endDate)
  }

  // For non-recurring events, compare full dates using numeric comparison
  const dateNum = toFullDateNumber(date.year, dateMonth, date.day)
  const startNum = toFullDateNumber(
    startDate.year,
    startDate.month,
    startDate.day
  )
  const endNum = toFullDateNumber(endDate.year, endDate.month, endDate.day)

  return dateNum >= startNum && dateNum <= endNum
}

/**
 * Check if a date is blacked out based on the blackout configuration.
 * A date is blacked out if:
 * - It falls on a blacked out day of the week, OR
 * - It falls within a blackout event's date range
 */
export const isDateBlackedOut = (
  date: CalendarDate,
  blackoutConfig: ShippingBlackoutConfig | null | undefined,
  timezone: string = getLocalTimeZone()
): boolean => {
  if (!blackoutConfig) {
    return false
  }

  // Check if the date falls on a blacked out day of week
  if (blackoutConfig.blackoutDaysOfWeek?.length) {
    const dayOfWeek = date.toDate(timezone).getDay()
    const enumDayOfWeek = dayOfWeekMap[dayOfWeek]
    if (
      enumDayOfWeek &&
      blackoutConfig.blackoutDaysOfWeek.includes(enumDayOfWeek)
    ) {
      return true
    }
  }

  // Check if the date falls within any blackout event
  if (blackoutConfig.blackoutEvents?.length) {
    for (const event of blackoutConfig.blackoutEvents) {
      if (
        isDateInBlackoutEvent(
          date,
          event.startDate,
          event.endDate,
          event.isRecurring
        )
      ) {
        return true
      }
    }
  }

  return false
}
